import type {
  DicomClassification,
  DicomGeometryStatus,
  DicomPixelDataStatus,
  DicomSeriesAnalysis,
  PreparedNiftiAsset,
} from '../types/dicom';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

export type DisplayDicomSeries = {
  alignedNiftiAsset?: PreparedNiftiAsset;
  /** Presentation only: pending edits must not overwrite saved asset metadata. */
  hasUnsavedAlignment?: boolean;
  id: string;
  studyInstanceUid: string;
  seriesInstanceUid: string;
  studyDate?: string | null;
  studyDescription?: string | null;
  seriesNumber?: number | null;
  modality: DicomClassification;
  label: string;
  slices: number;
  instanceCount?: number;
  totalFrameCount?: number;
  countLabel?: string;
  type: 'MR' | 'CT';
  tr?: string;
  te?: string;
  dim?: '2D' | '3D';
  matchLevel: 'High match' | 'Uncertain';
  thumbIndex: number;
  eligibleForFixed: boolean;
  isVolumeCandidate?: boolean;
  geometryStatus?: DicomGeometryStatus;
  geometryIssues?: string[];
  pixelDataStatus?: DicomPixelDataStatus;
  pixelDataIssues?: string[];
  classificationReasons?: string[];
  rows?: number | null;
  columns?: number | null;
  pixelSpacing?: number[] | null;
  thumbnailDataUrls?: string[];
  title: string;
};

export function buildDicomSeriesKey(studyInstanceUid: string, seriesInstanceUid: string) {
  return `${studyInstanceUid}:${seriesInstanceUid}`;
}

export function isReferenceMrSeries(series: Pick<DisplayDicomSeries, 'type' | 'modality'>) {
  return series.type === 'MR' && (series.modality === 'T1w' || series.modality === 'T2w');
}

export function isProjectReferenceSeries(series: Pick<DisplayDicomSeries, 'type' | 'modality'>) {
  return isReferenceMrSeries(series) || (series.type === 'CT' && series.modality === 'CT');
}

function formatMilliseconds(value: number | null) {
  if (value === null || !Number.isFinite(value)) {
    return undefined;
  }

  return `${value} ms`;
}

function mapModalityType(modality: string): 'MR' | 'CT' {
  return modality.toUpperCase() === 'CT' ? 'CT' : 'MR';
}

function mapAcquisitionDimension(value: string | null): '2D' | '3D' | undefined {
  if (value === '2D' || value === '3D') {
    return value;
  }

  return undefined;
}

function resolveSeriesLabel(series: DicomSeriesAnalysis) {
  if (series.series_description) {
    return series.series_description;
  }

  if (series.protocol_name) {
    return series.protocol_name;
  }

  if (series.series_number !== null) {
    return `Series ${series.series_number}`;
  }

  return 'Unnamed MR series';
}

export function mapApiSeriesToDisplaySeries(
  series: DicomSeriesAnalysis,
  index: number
): DisplayDicomSeries {
  const label = resolveSeriesLabel(series);
  const totalFrameCount = series.total_frame_count ?? series.instance_count;
  const isMultiFrame =
    (series.number_of_frames ?? 1) > 1 || totalFrameCount > series.instance_count;
  const instanceLabel = `${series.instance_count} instance${series.instance_count === 1 ? '' : 's'}`;
  const frameLabel = `${totalFrameCount} frame${totalFrameCount === 1 ? '' : 's'}`;
  const countLabel = isMultiFrame
    ? `${instanceLabel} / ${frameLabel}`
    : `${totalFrameCount} slices`;
  const isLegacyAnalysis = series.is_volume_candidate === undefined;
  const isVolumeCandidate = series.is_volume_candidate ?? series.eligible_for_fixed;
  const geometryStatus = series.geometry_status ?? 'warning';
  const geometryIssues =
    series.geometry_issues ??
    (isLegacyAnalysis ? ['Geometry validation is unavailable for this existing record.'] : []);

  return {
    id: buildDicomSeriesKey(series.study_instance_uid, series.series_instance_uid),
    studyInstanceUid: series.study_instance_uid,
    seriesInstanceUid: series.series_instance_uid,
    studyDate: series.study_date,
    studyDescription: series.study_description,
    seriesNumber: series.series_number,
    modality: series.classification,
    label,
    slices: totalFrameCount,
    instanceCount: series.instance_count,
    totalFrameCount,
    countLabel,
    type: mapModalityType(series.modality),
    tr: formatMilliseconds(series.repetition_time),
    te: formatMilliseconds(series.echo_time),
    dim: mapAcquisitionDimension(series.mr_acquisition_type),
    matchLevel:
      series.classification_confidence === 'high' &&
      (series.classification === 'T1w' || series.classification === 'T2w')
        ? 'High match'
        : 'Uncertain',
    thumbIndex: index,
    eligibleForFixed: isVolumeCandidate && (
      (series.modality.toUpperCase() === 'CT' && series.classification === 'CT') ||
      (series.eligible_for_fixed && series.modality.toUpperCase() === 'MR' &&
        (series.classification === 'T1w' || series.classification === 'T2w'))
    ),
    isVolumeCandidate,
    geometryStatus,
    geometryIssues,
    pixelDataStatus: series.pixel_data_status ?? 'not_checked',
    pixelDataIssues: series.pixel_data_issues ?? [],
    classificationReasons: series.classification_reasons,
    rows: series.rows,
    columns: series.columns,
    pixelSpacing: series.pixel_spacing,
    thumbnailDataUrls: series.thumbnail_data_urls,
    title: `${label} · ${formatDateDDMMYYYY(series.study_date, 'Unknown date')}`,
  };
}
